import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Plus, X, Upload } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";

const empty = {
  name: "",
  quality_id: "",
  collection_id: "",
  short_description: "",
  long_description: "",
  cover_image: "",
  gallery_images: [],
  active: true,
  in_collection_showcase: false,
  internal_note: "",
  variants: [],
};

export default function ProductForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(empty);
  const [collections, setCollections] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/collections").then(({ data }) => setCollections(data));
    api.get("/admin/qualities").then(({ data }) => setQualities(data));
    if (id) {
      api.get(`/admin/products/${id}`).then(({ data }) => {
        setData({
          ...data,
          quality_id: data.quality_id || "",
          collection_id: data.collection_id || "",
          variants: (data.variants || []).map((v) => ({
            id: v.id,
            variant_type: v.variant_type || "READY_SIZE",
            label: v.label,
            width_cm: v.width_cm,
            height_cm: v.height_cm,
            stock_amount: v.stock_amount,
            active: v.active !== false,
          })),
        });
      });
    }
  }, [id]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const addVariant = (variant_type) => {
    setData((d) => ({
      ...d,
      variants: [
        ...d.variants,
        {
          variant_type,
          label: "",
          width_cm: 0,
          height_cm: variant_type === "READY_SIZE" ? 0 : null,
          stock_amount: 0,
          active: true,
        },
      ],
    }));
  };

  const updateVariant = (i, k, v) =>
    setData((d) => ({
      ...d,
      variants: d.variants.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)),
    }));

  const removeVariant = (i) =>
    setData((d) => ({ ...d, variants: d.variants.filter((_, idx) => idx !== i) }));

  const upload = async (e, target) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data: r } = await api.post("/admin/upload", fd);
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.url}`;
      if (target === "cover") set("cover_image", url);
      else set("gallery_images", [...data.gallery_images, url]);
      toast.success("Yüklendi");
    } catch {
      toast.error("Yükleme başarısız");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name) {
      toast.error("Ürün adı zorunlu");
      return;
    }
    if (!data.quality_id) {
      toast.error("Kalite seçimi zorunludur");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...data,
        collection_id: data.collection_id || null,
        quality_id: data.quality_id || null,
      };
      if (id) {
        await api.put(`/admin/products/${id}`, payload);
        toast.success("Güncellendi");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Ürün oluşturuldu");
      }
      nav("/admin/urunler");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-product-form">
      <Link to="/admin/urunler" className="inline-flex items-center gap-2 text-sm text-[#6b655c] hover:text-[#c8a97e]">
        <ArrowLeft size={14} /> Ürünlere dön
      </Link>
      <div>
        <span className="label-tag">{id ? "Düzenle" : "Yeni"}</span>
        <h1 className="font-serif text-4xl mt-2">{id ? "Ürünü Düzenle" : "Yeni Ürün"}</h1>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-[#eae5dc] p-6 space-y-5">
            <Field label="Ürün Adı *">
              <Input value={data.name} onChange={(e) => set("name", e.target.value)} data-testid="product-form-name" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Kalite (Kategori) *">
                <Select value={data.quality_id || ""} onValueChange={(v) => set("quality_id", v)}>
                  <SelectTrigger data-testid="product-form-quality"><SelectValue placeholder="Kalite seç" /></SelectTrigger>
                  <SelectContent>
                    {qualities.length === 0 && <div className="px-3 py-2 text-xs text-[#6b655c]">Önce kalite oluşturun</div>}
                    {qualities.map((q) => <SelectItem key={q.id} value={q.id}>{q.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Koleksiyon (opsiyonel)">
                <Select value={data.collection_id || "none"} onValueChange={(v) => set("collection_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="product-form-collection"><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Yok —</SelectItem>
                    {collections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Kısa Açıklama">
              <Input value={data.short_description} onChange={(e) => set("short_description", e.target.value)} />
            </Field>
            <Field label="Detay Açıklama">
              <Textarea rows={4} value={data.long_description} onChange={(e) => set("long_description", e.target.value)} />
            </Field>
            <Field label="İç Not (yalnız admin)">
              <Textarea rows={2} value={data.internal_note} onChange={(e) => set("internal_note", e.target.value)} />
            </Field>
          </div>

          <div className="space-y-6">
            <div className="bg-white border border-[#eae5dc] p-6 space-y-4">
              <h3 className="font-serif text-lg">Görseller</h3>
              <ImageUpload
                label="Kapak Görseli"
                value={data.cover_image}
                onChange={(url) => set("cover_image", url)}
                testid="product-form-cover"
              />
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6b655c] block mb-2">Galeri</label>
                <label className="btn-luxe-outline text-xs cursor-pointer w-full justify-center">
                  <Upload size={14} /> Galeriye Ekle
                  <input type="file" hidden accept="image/*" onChange={(e) => upload(e, "gallery")} />
                </label>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {(data.gallery_images || []).map((g, i) => (
                    <div key={i} className="relative group">
                      <img src={g} alt="" className="w-full aspect-square object-cover" />
                      <button
                        type="button"
                        onClick={() => set("gallery_images", data.gallery_images.filter((_, x) => x !== i))}
                        className="absolute top-1 right-1 bg-black/70 text-white p-1 opacity-0 group-hover:opacity-100"
                      ><X size={12} /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#eae5dc] p-6 space-y-4">
              <h3 className="font-serif text-lg">Görünürlük</h3>
              <Toggle label="Aktif" checked={data.active} onChange={(v) => set("active", v)} testid="product-form-active" />
              <Toggle label="Vitrinde Göster" checked={data.in_collection_showcase} onChange={(v) => set("in_collection_showcase", v)} testid="product-form-showcase" />
            </div>
          </div>
        </div>

        {/* Variants — split into two sections */}
        <div className="bg-white border border-[#eae5dc] p-6 space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-serif text-xl">Hazır Ebat Ölçüler</h3>
              <p className="text-xs text-[#6b655c] mt-1">Bayinin doğrudan adet seçip sipariş verebileceği hazır ebatlar.</p>
            </div>
            <Button type="button" onClick={() => addVariant("READY_SIZE")} variant="outline" size="sm" data-testid="product-form-add-ready"><Plus size={14}/> Ebat Ekle</Button>
          </div>
          <div className="space-y-3 mt-3">
            {data.variants.filter((v) => v.variant_type === "READY_SIZE").length === 0 && (
              <p className="text-[#6b655c] text-sm py-3 border border-dashed border-[#eae5dc] text-center">Hazır ebat eklenmedi.</p>
            )}
            {data.variants.map((v, i) => v.variant_type !== "READY_SIZE" ? null : (
              <div key={i} className="grid grid-cols-12 gap-3 items-end pb-3 border-b border-[#f0ebe2]">
                <div className="col-span-12 sm:col-span-3">
                  <label className="text-xs uppercase tracking-wider text-[#6b655c]">Etiket</label>
                  <Input value={v.label} onChange={(e) => updateVariant(i, "label", e.target.value)} placeholder="örn: 80x150" data-testid={`ready-${i}-label`} />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <label className="text-xs uppercase tracking-wider text-[#6b655c]">En (cm)</label>
                  <Input type="number" value={v.width_cm ?? ""} onChange={(e) => updateVariant(i, "width_cm", parseFloat(e.target.value) || 0)} />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <label className="text-xs uppercase tracking-wider text-[#6b655c]">Boy (cm)</label>
                  <Input type="number" value={v.height_cm ?? ""} onChange={(e) => updateVariant(i, "height_cm", parseFloat(e.target.value) || 0)} />
                </div>
                <div className="col-span-4 sm:col-span-3">
                  <label className="text-xs uppercase tracking-wider text-[#6b655c]">Stok (adet)</label>
                  <Input type="number" value={v.stock_amount} onChange={(e) => updateVariant(i, "stock_amount", parseFloat(e.target.value) || 0)} />
                </div>
                <div className="col-span-8 sm:col-span-1 flex items-center pb-2">
                  <Switch checked={v.active} onCheckedChange={(c) => updateVariant(i, "active", c)} />
                </div>
                <div className="col-span-4 sm:col-span-1 text-right">
                  <button type="button" onClick={() => removeVariant(i)} className="p-2 hover:bg-red-50 text-red-600"><X size={14}/></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#eae5dc] p-6 space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-serif text-xl">Özel Kesim Ölçüler</h3>
              <p className="text-xs text-[#6b655c] mt-1">En seçimli, bayinin boy/metre girdiği ve onay öncesi şekil/kenar/saçak detayı seçmesi gereken varyantlar. Stok metre cinsindendir.</p>
            </div>
            <Button type="button" onClick={() => addVariant("WIDTH_OPTION")} variant="outline" size="sm" data-testid="product-form-add-custom"><Plus size={14}/> En Seçeneği Ekle</Button>
          </div>
          <div className="space-y-3 mt-3">
            {data.variants.filter((v) => v.variant_type === "WIDTH_OPTION").length === 0 && (
              <p className="text-[#6b655c] text-sm py-3 border border-dashed border-[#eae5dc] text-center">Özel kesim ebatı eklenmedi.</p>
            )}
            {data.variants.map((v, i) => v.variant_type !== "WIDTH_OPTION" ? null : (
              <div key={i} className="grid grid-cols-12 gap-3 items-end pb-3 border-b border-[#f0ebe2]">
                <div className="col-span-12 sm:col-span-4">
                  <label className="text-xs uppercase tracking-wider text-[#6b655c]">Etiket</label>
                  <Input value={v.label} onChange={(e) => updateVariant(i, "label", e.target.value)} placeholder="örn: 160 cm en" data-testid={`custom-${i}-label`} />
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <label className="text-xs uppercase tracking-wider text-[#6b655c]">En (cm)</label>
                  <Input type="number" value={v.width_cm ?? ""} onChange={(e) => updateVariant(i, "width_cm", parseFloat(e.target.value) || 0)} />
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <label className="text-xs uppercase tracking-wider text-[#6b655c]">Stok (metre)</label>
                  <Input type="number" step="0.1" value={v.stock_amount} onChange={(e) => updateVariant(i, "stock_amount", parseFloat(e.target.value) || 0)} />
                </div>
                <div className="col-span-8 sm:col-span-1 flex items-center pb-2">
                  <Switch checked={v.active} onCheckedChange={(c) => updateVariant(i, "active", c)} />
                </div>
                <div className="col-span-4 sm:col-span-1 text-right">
                  <button type="button" onClick={() => removeVariant(i)} className="p-2 hover:bg-red-50 text-red-600"><X size={14}/></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <Button type="submit" disabled={saving} className="btn-luxe" data-testid="product-form-save-btn">
            {saving ? "Kaydediliyor…" : id ? "Güncelle" : "Oluştur"}
          </Button>
          <Link to="/admin/urunler" className="btn-luxe-outline text-xs">Vazgeç</Link>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-[#6b655c] block mb-1.5">{label}</label>
    {children}
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm">{label}</span>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);
